import React, { useEffect, useState } from 'react';
import { Plus, ArrowLeft, PenLine, Trash2, AlertTriangle, Sparkles } from 'lucide-react';
import Modal from './Modal';
import { api, DEMO } from '../lib/api';
import { hasCap } from '../lib/capabilities';
import AiNoteGeneratorModal from './AiNoteGeneratorModal';

const GROUP_STATUS_LABELS = { draft: 'Draft', scheduled: 'Scheduled', in_progress: 'In progress', completed: 'Completed', cancelled: 'Cancelled' };
const GROUP_STATUS_BADGE = { draft: 'badge-warn', scheduled: 'badge-neutral', in_progress: 'badge-warn', completed: 'badge-ok', cancelled: 'badge-neutral' };
const ATTENDANCE_LABELS = { present: 'Present', late: 'Late', left_early: 'Left early', absent: 'Absent', no_show: 'No-show', excused: 'Excused' };
const ATTENDANCE_BADGE = { present: 'badge-ok', late: 'badge-ok', left_early: 'badge-warn', absent: 'badge-neutral', no_show: 'badge-danger', excused: 'badge-neutral' };
const ASSIGNMENT_LABELS = { assigned: 'Assigned', removed_before_session: 'Removed before session', cancelled: 'Cancelled', entered_in_error: 'Entered in error' };
const NOTE_STATUS_LABELS = { draft: 'Draft', ready_for_signature: 'Ready to sign', signed: 'Signed', voided: 'Voided' };
const NOTE_STATUS_BADGE = { draft: 'badge-warn', ready_for_signature: 'badge-warn', signed: 'badge-ok', voided: 'badge-neutral' };
const PARTICIPATION_LEVELS = ['active', 'moderate', 'limited', 'minimal', 'declined', 'disruptive', 'other'];
const PROGRESS_RATINGS = ['improving', 'unchanged', 'worsening', 'unable_to_assess'];

export default function PhpGroupSessionDetail({ groupId, user, notify, navigate }) {
  const canManage = hasCap(user, 'therapy.manage');
  const [detail, setDetail] = useState(null);
  const [busy, setBusy] = useState(false);
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState(null); // roster row being edited (response)
  const [removing, setRemoving] = useState(null); // roster row being soft-removed

  async function load() {
    try {
      if (DEMO) { setDetail(null); return; }
      setDetail(await api(`/php/group-sessions/${groupId}`));
    } catch (err) { notify(err.message); }
  }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [groupId]);

  if (DEMO) return (
    <section className="content-card">
      <div className="php-safety-alert" style={{ borderColor: '#b45309', background: '#fffbeb', color: '#92400e' }}><div><strong>Group detail requires live data.</strong><p>Roster, attendance, and individualized responses are computed server-side and are available in the authenticated (non-demo) workspace.</p></div></div>
    </section>
  );
  if (!detail) return <section className="content-card"><p className="empty-copy">Loading group…</p></section>;

  const { session, roster, assigned_count, capacity, signed_count, duplicate_warning } = detail;
  const pct = capacity > 0 ? Math.min(100, Math.round((assigned_count / capacity) * 100)) : 0;

  async function complete() {
    setBusy(true);
    try {
      await api(`/php/group-sessions/${groupId}`, { method: 'PATCH', body: { ...session, status: 'completed' } });
      notify('Group marked complete.');
      await load();
    } catch (err) { notify(err.message); } finally { setBusy(false); }
  }

  return (
    <section className="content-card">
      <div className="page-heading">
        <div>
          <button className="text-button" onClick={() => navigate('/php-therapy')}><ArrowLeft size={14}/> <span>Back to therapy</span></button>
          <div className="eyebrow">PHP / IOP / PSR GROUP PSYCHOTHERAPY</div>
          <h1>{session.name}</h1>
          <p>{session.topic ? `${session.topic}. ` : ''}{session.clinical_population || 'No clinical population described.'}</p>
        </div>
      </div>

      <div className="therapy-section-head">
        <div className="therapy-capacity">
          <span className="therapy-capacity-label"><strong>{assigned_count}</strong> / {capacity} participants</span>
          <div className="therapy-capacity-bar"><div className={`therapy-capacity-fill ${assigned_count >= capacity ? 'full' : ''}`} style={{ width: `${pct}%` }} /></div>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <span className={`badge ${GROUP_STATUS_BADGE[session.status] || 'badge-neutral'}`}>{GROUP_STATUS_LABELS[session.status] || session.status}</span>
          <span className="badge neutral">{signed_count} signed</span>
          {canManage && session.status !== 'completed' && session.status !== 'cancelled' && <button className="button ghost" disabled={busy} onClick={complete}><span>Complete group</span></button>}
          {canManage && <button className="button primary" onClick={() => setAdding(true)}><Plus size={14}/> <span>Add patient</span></button>}
        </div>
      </div>

      <div className="php-ov-grid" style={{ marginBottom: 12 }}>
        <div className="php-ov-card"><span className="php-ov-label">Scheduled</span><div className="php-ov-value">{session.session_start ? new Date(session.session_start).toLocaleString() : '—'} → {session.session_end ? new Date(session.session_end).toLocaleTimeString() : '—'}</div></div>
        <div className="php-ov-card"><span className="php-ov-label">Facilitator</span><div className="php-ov-value">{session.facilitator_name || '—'}{session.co_facilitator_name ? ` + ${session.co_facilitator_name}` : ''}</div></div>
        <div className="php-ov-card"><span className="php-ov-label">Method</span><div className="php-ov-value">{session.therapeutic_method || '—'}</div></div>
        <div className="php-ov-card"><span className="php-ov-label">Expected outcome</span><div className="php-ov-value">{session.expected_outcome || '—'}</div></div>
      </div>

      {session.material_discussed && <p><strong>Material discussed.</strong> {session.material_discussed}</p>}
      {session.skills_practiced && <p><strong>Skills practiced.</strong> {session.skills_practiced}</p>}
      {session.overall_group_process && <p><strong>Group process.</strong> {session.overall_group_process}</p>}

      {duplicate_warning && duplicate_warning.length > 0 && (
        <div className="therapy-warning" role="status"><AlertTriangle size={18}/><div><strong>Substantially similar responses detected.</strong><p>Multiple participant responses appear substantially similar. Confirm that each patient's response accurately reflects the individual clinical encounter before signing.</p></div></div>
      )}

      <div className="section-space">
        <div className="content-heading"><div><h4>Roster</h4><p>Attendance is per-participant. Absent / no-show / excused are preserved as records but never produce a rendered therapeutic service.</p></div></div>
        {roster.length === 0 && <p className="empty-copy">No participants yet.</p>}
        {roster.map(r => (
          <div key={r.id} className="therapy-roster-row">
            <div className="who">
              <strong>{r.first_name} {r.last_name}</strong>
              <small>MRN {r.mrn || '—'}</small>
            </div>
            <div className="chips">
              <span className={`badge ${ASSIGNMENT_LABELS[r.assignment_status] ? 'badge-neutral' : 'badge-neutral'}`}>{ASSIGNMENT_LABELS[r.assignment_status] || r.assignment_status}</span>
              {r.assignment_status === 'assigned' && <span className={`badge ${ATTENDANCE_BADGE[r.attendance_status] || 'badge-warn'}`}>{r.attendance_status ? ATTENDANCE_LABELS[r.attendance_status] : 'Unmarked'}</span>}
              {r.assignment_status === 'assigned' && <span className={`badge ${NOTE_STATUS_BADGE[r.status] || 'badge-neutral'}`}>{NOTE_STATUS_LABELS[r.status] || r.status}</span>}
            </div>
            {canManage && r.assignment_status === 'assigned' && <button className="button ghost small" onClick={() => setEditing(r)}><PenLine size={13}/> <span>Document</span></button>}
            {canManage && r.assignment_status === 'assigned' && <button className="button ghost small" onClick={() => setRemoving(r)}><Trash2 size={13}/> <span>Remove</span></button>}
          </div>
        ))}
      </div>

      {adding && <AddPatientModal groupId={groupId} programType={session.program_type} onClose={() => setAdding(false)} onAdded={async () => { setAdding(false); await load(); }} notify={notify} />}
      {editing && <ResponseEditor session={session} row={editing} onClose={() => setEditing(null)} onSaved={async () => { setEditing(null); await load(); }} notify={notify} />}
      {removing && <RemoveModal groupId={groupId} row={removing} onClose={() => setRemoving(null)} onRemoved={async () => { setRemoving(null); await load(); }} notify={notify} />}
    </section>
  );
}

function AddPatientModal({ groupId, programType, onClose, onAdded, notify }) {
  const [patients, setPatients] = useState([]);
  const [enrollments, setEnrollments] = useState([]);
  const [patientId, setPatientId] = useState('');
  const [enrollmentId, setEnrollmentId] = useState('');
  const [clinicalRelevance, setClinicalRelevance] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api('/patients').then(d => setPatients(d.patients || [])).catch(e => notify(e.message));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  async function pickPatient(id) {
    setPatientId(id); setEnrollmentId('');
    try {
      const e = await api(`/patients/${id}/php/enrollments`);
      setEnrollments((e.enrollments || []).filter(en => en.status === 'active' && en.program_type === programType));
    } catch (err) { notify(err.message); }
  }

  async function submit(e) {
    e.preventDefault(); setBusy(true);
    try {
      if (!patientId) throw new Error('Choose a patient.');
      if (!enrollmentId) throw new Error('Choose an active enrollment matching this group.');
      if (!clinicalRelevance.trim()) throw new Error('A clinical relevance note (why this patient fits this group) is required.');
      await api(`/php/group-sessions/${groupId}/participants`, { method: 'POST', body: { patient_id: patientId, enrollment_id: enrollmentId, clinical_relevance: clinicalRelevance.trim() } });
      notify('Patient added to group.');
      await onAdded();
    } catch (err) { notify(err.message); } finally { setBusy(false); }
  }

  return (
    <Modal title="Add patient to group" onClose={onClose}>
      <form className="php-form" onSubmit={submit}>
        <div className="form-grid">
          <label className="span2">Patient
            <select value={patientId} onChange={e => pickPatient(e.target.value)}>
              <option value="">— Select —</option>
              {patients.map(p => <option key={p.id} value={p.id}>{p.first_name} {p.last_name}{p.mrn ? ` · MRN ${p.mrn}` : ''}</option>)}
            </select>
          </label>
          <label className="span2">Enrollment
            <select value={enrollmentId} onChange={e => setEnrollmentId(e.target.value)} disabled={!patientId}>
              <option value="">— Select —</option>
              {enrollments.map(en => <option key={en.id} value={en.id}>{en.program_type} · admitted {en.admitted_at}</option>)}
            </select>
          </label>
          <label className="span2">Why this patient is clinically relevant to this group<textarea rows={3} value={clinicalRelevance} onChange={e => setClinicalRelevance(e.target.value)} maxLength={2000} placeholder="Link to the patient's treatment plan or describe the clinical fit…" /></label>
        </div>
        <div className="php-plan-actions">
          <button className="button primary" disabled={busy}><span>{busy ? 'Adding…' : 'Add patient'}</span></button>
          <button type="button" className="button ghost" onClick={onClose}><span>Cancel</span></button>
        </div>
      </form>
    </Modal>
  );
}

function ResponseEditor({ session, row, onClose, onSaved, notify }) {
  const [form, setForm] = useState({
    attendance_status: row.attendance_status || '', presentation: row.presentation || '', participation: row.participation || '',
    individualized_response: row.individualized_response || '', relevant_verbalization: row.relevant_verbalization || '',
    progress: row.progress || '', continued_need: row.continued_need || '', plan: row.plan || '',
    attitude: row.attitude || '', concentration: row.concentration || '', follow_directions: row.follow_directions || '',
    peer_interaction: row.peer_interaction || '', participation_level: row.participation_level || '',
    response_to_intervention: row.response_to_intervention || '', progress_rating: row.progress_rating || '',
    clinical_barriers: row.clinical_barriers || '', safety_concern: !!row.safety_concern, safety_note: row.safety_note || '',
    status: row.status === 'signed' || row.status === 'voided' ? row.status : 'draft',
  });
  const [busy, setBusy] = useState(false);
  const [showAiModal, setShowAiModal] = useState(false);
  const immutable = row.status === 'signed' || row.status === 'voided';

  const set = k => e => setForm(f => ({ ...f, [k]: e.target.type === 'checkbox' ? e.target.checked : e.target.value }));

  async function submit(e) {
    e.preventDefault(); setBusy(true);
    try {
      await api(`/patients/${row.patient_id}/php/group-responses`, {
        method: 'POST',
        body: { ...form, group_session_id: session.id, enrollment_id: row.enrollment_id },
      });
      notify('Individualized response saved.');
      await onSaved();
    } catch (err) { notify(err.message); } finally { setBusy(false); }
  }

  async function sign() {
    setBusy(true);
    try {
      await api(`/patients/${row.patient_id}/php/group-responses/${row.id}/sign`, { method: 'POST', body: {} });
      notify('Response signed.');
      await onSaved();
    } catch (err) { notify(err.message); } finally { setBusy(false); }
  }

  async function addendum() {
    setBusy(true);
    try {
      await api(`/patients/${row.patient_id}/php/group-responses/${row.id}/addendum`, { method: 'POST', body: {} });
      notify('Addendum recorded as a new version.');
      await onSaved();
    } catch (err) { notify(err.message); } finally { setBusy(false); }
  }

  return (
    <Modal title={`Group therapy note — ${row.first_name} ${row.last_name}`} onClose={onClose} wide>
      {immutable && <div className="therapy-addendum"><div className="meta">This note is {row.status}. Content is immutable; record an addendum to correct it.</div></div>}
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 12 }}>
        {!immutable && (
          <button type="button" className="button ghost small" onClick={() => setShowAiModal(true)}>
            <Sparkles size={14} /> <span>AI Assist</span>
          </button>
        )}
      </div>
      <form className="php-form" onSubmit={submit}>
        <div className="form-grid">
          <label>Attendance
            <select value={form.attendance_status} onChange={set('attendance_status')} disabled={immutable}>
              <option value="">— Unmarked —</option>
              {Object.entries(ATTENDANCE_LABELS).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
            </select>
          </label>
          <label>Participation level
            <select value={form.participation_level} onChange={set('participation_level')} disabled={immutable}>
              <option value="">— Unset —</option>
              {PARTICIPATION_LEVELS.map(l => <option key={l} value={l}>{l}</option>)}
            </select>
          </label>
          <label>Progress rating
            <select value={form.progress_rating} onChange={set('progress_rating')} disabled={immutable}>
              <option value="">— Unset —</option>
              {PROGRESS_RATINGS.map(l => <option key={l} value={l}>{l}</option>)}
            </select>
          </label>
          <label>Note status
            <select value={form.status} onChange={set('status')} disabled={immutable}>
              <option value="draft">Draft</option>
              <option value="ready_for_signature">Ready to sign</option>
              <option value="signed">Signed</option>
            </select>
          </label>
          <label className="span2">Assessment (A)<textarea rows={2} value={form.presentation} onChange={set('presentation')} disabled={immutable} maxLength={8000} placeholder="Presentation and mental status" /></label>
          <label className="span2">Intervention (I)<textarea rows={2} value={form.response_to_intervention} onChange={set('response_to_intervention')} disabled={immutable} maxLength={8000} placeholder="Interventions used during group" /></label>
          <label className="span2">Individualized response (R) *<textarea rows={3} value={form.individualized_response} onChange={set('individualized_response')} disabled={immutable} maxLength={8000} placeholder="Each participant's response is clinician-authored — never cloned from a shared note." required /></label>
          <label className="span2">Evaluation (E) / Progress<textarea rows={2} value={form.progress} onChange={set('progress')} disabled={immutable} maxLength={8000} placeholder="Evaluation of response toward goals" /></label>
          <label className="span2">Relevant verbalization<textarea rows={2} value={form.relevant_verbalization} onChange={set('relevant_verbalization')} disabled={immutable} maxLength={8000} /></label>
          <label className="span2">Continued need<textarea rows={2} value={form.continued_need} onChange={set('continued_need')} disabled={immutable} maxLength={8000} /></label>
          <label>Attitude<textarea rows={1} value={form.attitude} onChange={set('attitude')} disabled={immutable} maxLength={8000} /></label>
          <label>Concentration<textarea rows={1} value={form.concentration} onChange={set('concentration')} disabled={immutable} maxLength={8000} /></label>
          <label>Follow directions<textarea rows={1} value={form.follow_directions} onChange={set('follow_directions')} disabled={immutable} maxLength={8000} /></label>
          <label>Peer interaction<textarea rows={1} value={form.peer_interaction} onChange={set('peer_interaction')} disabled={immutable} maxLength={8000} /></label>
          <label className="span2">Clinical barriers<textarea rows={2} value={form.clinical_barriers} onChange={set('clinical_barriers')} disabled={immutable} maxLength={8000} /></label>
          <label className="span2">Plan<textarea rows={2} value={form.plan} onChange={set('plan')} disabled={immutable} maxLength={8000} /></label>
          <label className="checkbox-label"><input type="checkbox" checked={form.safety_concern} onChange={set('safety_concern')} disabled={immutable} /> <span>Safety concern documented</span></label>
          {form.safety_concern && <label className="span2">Safety note<textarea rows={2} value={form.safety_note} onChange={set('safety_note')} disabled={immutable} maxLength={8000} /></label>}
        </div>
        <div className="php-plan-actions">
          {!immutable && <button className="button primary" disabled={busy}><span>{busy ? 'Saving…' : 'Save note'}</span></button>}
          {!immutable && (form.status === 'ready_for_signature' || form.status === 'draft') && <button type="button" className="button ghost" disabled={busy} onClick={sign}><span>Sign</span></button>}
          {immutable && row.status === 'signed' && <button type="button" className="button ghost" disabled={busy} onClick={addendum}><PenLine size={14}/> <span>Addendum</span></button>}
          <button type="button" className="button ghost" onClick={onClose}><span>Close</span></button>
        </div>
      </form>
      {showAiModal && (
        <AiNoteGeneratorModal
          patient={{ id: row.patient_id, name: `${row.first_name} ${row.last_name}` }}
          initialNoteType="group_therapy_note"
          programType={session.program_type || 'php'}
          onClose={() => setShowAiModal(false)}
          notify={notify}
          onApply={draft => {
            setForm(f => ({
              ...f,
              presentation: draft.presentation || draft.assessment || f.presentation,
              response_to_intervention: draft.response_to_intervention || draft.intervention || f.response_to_intervention,
              individualized_response: draft.individualized_response || draft.response || f.individualized_response,
              progress: draft.progress || draft.evaluation || f.progress,
              relevant_verbalization: draft.relevant_verbalization || f.relevant_verbalization,
              attitude: draft.attitude || f.attitude,
              concentration: draft.concentration || f.concentration,
              follow_directions: draft.follow_directions || f.follow_directions,
              peer_interaction: draft.peer_interaction || f.peer_interaction,
              clinical_barriers: draft.clinical_barriers || f.clinical_barriers,
              continued_need: draft.continued_need || f.continued_need,
              plan: draft.plan || f.plan,
              attendance_status: draft.attendance_status || f.attendance_status,
              participation_level: draft.participation_level || f.participation_level,
              progress_rating: draft.progress_rating || f.progress_rating,
              safety_concern: draft.safety_concern !== undefined ? draft.safety_concern : f.safety_concern,
              safety_note: draft.safety_note ?? f.safety_note,
            }));
          }}
        />
      )}
    </Modal>
  );
}

function RemoveModal({ groupId, row, onClose, onRemoved, notify }) {
  const [status, setStatus] = useState('removed_before_session');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);

  async function submit(e) {
    e.preventDefault(); setBusy(true);
    try {
      if (!reason.trim()) throw new Error('A removal reason is required.');
      await api(`/php/group-sessions/${groupId}/participants/${row.patient_id}/cancel`, { method: 'POST', body: { assignment_status: status, removal_reason: reason.trim() } });
      notify('Participant removed (record preserved).');
      await onRemoved();
    } catch (err) { notify(err.message); } finally { setBusy(false); }
  }

  return (
    <Modal title={`Remove ${row.first_name} ${row.last_name}`} onClose={onClose}>
      <div className="php-safety-alert" style={{ borderColor: '#e5e7eb', background: '#f9fafb', color: '#374151' }}><div><strong>Soft removal.</strong><p>The participant's record and history are preserved; this only changes their assignment status.</p></div></div>
      <form className="php-form" onSubmit={submit}>
        <div className="form-grid">
          <label>Removal status
            <select value={status} onChange={e => setStatus(e.target.value)}>
              <option value="removed_before_session">Removed before session</option>
              <option value="cancelled">Cancelled</option>
              <option value="entered_in_error">Entered in error</option>
            </select>
          </label>
          <label className="span2">Reason<textarea rows={3} value={reason} onChange={e => setReason(e.target.value)} maxLength={2000} /></label>
        </div>
        <div className="php-plan-actions">
          <button className="button primary" disabled={busy}>{busy ? 'Removing…' : 'Remove'}</button>
          <button type="button" className="button ghost" onClick={onClose}>Cancel</button>
        </div>
      </form>
    </Modal>
  );
}
